import React from 'react';
import { Info } from 'lucide-react';
import { Roll } from '../jarvis/Ring';
import { money, clamp01, fmtUnit, fmtUnitFull, StatusPill } from './shared';

/* Actual (solid) + projected-at-pace (hatched) against the target, with a "today" pace marker. */
function PaceBar({ b, color, testid }) {
  const scale = Math.max(b.target || 0, b.projected || 0, b.actual || 0, 1);
  const act = clamp01(b.actual / scale) * 100;
  const proj = clamp01(Math.max(b.projected, b.actual) / scale) * 100;
  const exp = b.target ? clamp01(b.expected_now / scale) * 100 : null;
  return (
    <div className="gpc-pace" style={{ '--tc': color }} data-testid={testid}>
      <div className="act" style={{ width: `${act}%` }} />
      {proj > act && <div className="proj" style={{ left: `${act}%`, width: `${proj - act}%` }} />}
      {exp != null && exp > 0 && <div className="exp" style={{ left: `${exp}%` }} data-l="TODAY" />}
    </div>
  );
}

function TargetCard({ title, tag, color, b, lead, extra, foot, testid }) {
  const unit = b?.unit || '$';
  if (!b) return null;
  return (
    <div className={`gpc-tcard${lead ? ' lead' : ''}`} style={{ '--tc': color }} data-testid={testid}>
      <div className="gpc-th"><h3><i />{title} <small>{tag}</small></h3><StatusPill block={b} testid={`${testid}-pill`} /></div>
      <div className="gpc-tbig">
        <strong><Roll value={fmtUnitFull(b.actual, unit)} /></strong>
        <span>of {b.target ? fmtUnitFull(b.target, unit) : '— no target'} · {b.pct?.toFixed(0)}%</span>
      </div>
      <PaceBar b={b} color={color} testid={`${testid}-bar`} />
      <div className="gpc-tgrid">
        <div><span>Remaining</span><b>{fmtUnitFull(b.remaining, unit)}</b></div>
        <div><span>Should be at by today</span><b className="dim">{fmtUnitFull(b.expected_now, unit)}</b></div>
        <div><span>Required / business day</span><b>{unit === 'files' ? `${(b.per_bd_required || 0).toFixed(1)}` : money(b.per_bd_required)}</b></div>
        <div><span>Running / business day</span><b className="dim">{unit === 'files' ? `${(b.per_bd_actual || 0).toFixed(1)}` : money(b.per_bd_actual)}</b></div>
        <div><span>Projected · at current pace</span><b style={{ color: b.projected_gap > 0 ? 'var(--amber)' : 'var(--green)' }}>{fmtUnitFull(b.projected, unit)}</b></div>
        <div><span>{b.projected_gap > 0 ? 'Projected shortfall' : 'Projected surplus'}</span><b style={{ color: b.projected_gap > 0 ? 'var(--amber)' : 'var(--green)' }}>{fmtUnitFull(Math.abs(b.projected_gap), unit)}</b></div>
        {extra}
      </div>
      <div className="gpc-tfoot"><span>{b.bd_left} of {b.bd_total} business days left</span><span>{foot}</span></div>
    </div>
  );
}

/* Week and month targets vs actual for lodgements (lead), approvals and settlements. */
export default function TargetsGrid({ cc }) {
  const L = cc.lodgements || {}, A = cc.approvals || {}, S = cc.settlements || {};
  const wk = cc.week || {};
  const row = (period, label) => (
    <div className="gpc-targets" data-testid={`gpc-targets-${period}`}>
      <TargetCard title="Lodgements" tag={label} color="var(--blue)" b={L[period]} lead testid={`gpc-t-lodge-${period}`}
        foot={L[`files_target_${period}`] ? `${L[period]?.count || 0} / ${L[`files_target_${period}`]} files` : `${L[period]?.count || 0} files`} />
      <TargetCard title="Approvals" tag={label} color="var(--cyan)" b={A[period]} testid={`gpc-t-appr-${period}`}
        extra={<div><span>Approved value</span><b className="dim">{money(period === 'month' ? A.amount_month : A.amount_week)}</b></div>}
        foot={period === 'month' ? `GCI ${money(A.gci_month)}` : ''} />
      <TargetCard title="Settlements" tag={label} color="var(--green)" b={S[period]} testid={`gpc-t-sett-${period}`}
        extra={<div><span>Booked to settle</span><b className="dim">{money(S[period]?.booked)} · {S[period]?.booked_count || 0} files</b></div>}
        foot={period === 'month' && S.month?.target ? (S.month.booked_gap > 0 ? `${money(S.month.booked_gap)} not yet booked` : 'Fully booked') : ''} />
    </div>
  );
  return (
    <section className="gpj-panel" data-testid="gpc-targets">
      <div className="gpj-panel-h"><h2>Targets vs actual</h2><p>{cc.month_name} · weekly target is a quarter of the monthly target · pace spread over business days</p></div>
      <div className="gpj-grid" style={{ gap: 16 }}>
        <div className="gpc-eyebrow" style={{ margin: 0 }}>This week <span>Week {wk.num} · {wk.range} · {wk.bd_left} business days left</span></div>
        {row('week', 'this week')}
        <div className="gpc-eyebrow" style={{ margin: '6px 0 0' }}>This month <span>{cc.month_name} · day {cc.business_days?.done} of {cc.business_days?.total}</span></div>
        {row('month', 'this month')}
      </div>
      <div className="gpc-leg">
        <span><i style={{ background: 'var(--blue)' }} />Actual</span>
        <span><i className="hatch" />Projected at current pace (not actual)</span>
        <span><i className="mark" />Where we should be today</span>
      </div>
      <div className="gpc-note"><Info /> Approval targets are file counts derived from the Set Goals funnel. Settlement and lodgement targets are dollars from Set Goals{L.target_explicit ? '' : ' (lodgement target follows the settlement volume target until a separate lodgement target is set)'}.</div>
    </section>
  );
}
